// Web component entry point for widget build
// This file is used when building with BUILD_TARGET=widget

import { createRoot } from 'react-dom/client'
import { ChatWidget, type WidgetMode } from './ChatWidget'
// Import CSS as string for Shadow DOM injection
import widgetStyles from './widget-styles.css?inline'

class EducationAssistantChatWidget extends HTMLElement {
  static get observedAttributes() {
    return ['api-url', 'mode']
  }

  private root: ReturnType<typeof createRoot> | null = null

  private getMode(): WidgetMode {
    const mode = this.getAttribute('mode')
    return mode === 'popup' ? 'popup' : 'embedded'
  }

  connectedCallback() {
    const shadow = this.attachShadow({ mode: 'open' })

    // Inject styles into Shadow DOM
    const styleElement = document.createElement('style')
    styleElement.textContent = widgetStyles
    shadow.appendChild(styleElement)

    // Create mount point
    const container = document.createElement('div')
    const mode = this.getMode()
    // Embedded mode: container fills parent so the chat takes full space
    // Popup mode: no size constraints, the floating widget uses fixed positioning
    container.style.cssText = mode === 'popup' ? '' : 'width: 100%; height: 100%;'
    shadow.appendChild(container)

    // Get attributes
    const apiUrl = this.getAttribute('api-url') || undefined

    // Mount React
    this.root = createRoot(container)
    this.root.render(<ChatWidget apiUrl={apiUrl} mode={mode} />)
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount()
      this.root = null
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue && this.root) {
      const container = this.shadowRoot?.querySelector('div')
      if (container) {
        const apiUrl = this.getAttribute('api-url') || undefined
        const mode = this.getMode()
        this.root.render(<ChatWidget apiUrl={apiUrl} mode={mode} />)
      }
    }
  }
}

customElements.define('education-assistant-chat-widget', EducationAssistantChatWidget)
